// Create a new file: pages/tasks/hooks/useExcelEditing.ts
import { useState, useCallback, useRef } from 'react';

interface UseExcelEditingProps {
  totalRows: number;
  totalCols: number;
  onCellEdit: (rowIndex: number, colIndex: number) => void;
  onCellSave: (rowIndex: number, colIndex: number) => void;
}

export const useExcelEditing = ({
  totalRows,
  totalCols,
  onCellEdit,
  onCellSave,
}: UseExcelEditingProps) => {
  const [currentCell, setCurrentCell] = useState({ row: 0, col: 0 });
  const [isEditing, setIsEditing] = useState(false);
  const pendingSaveRef = useRef(false);

  const startEditing = useCallback((row: number, col: number) => {
    setCurrentCell({ row, col });
    setIsEditing(true);
    onCellEdit(row, col);
  }, [onCellEdit]);

  const stopEditing = useCallback(() => {
    if (isEditing && !pendingSaveRef.current) {
      pendingSaveRef.current = true;
      onCellSave(currentCell.row, currentCell.col);
      setIsEditing(false);
      pendingSaveRef.current = false;
    }
  }, [isEditing, currentCell, onCellSave]);

  const moveToNextCell = useCallback((direction: 'next' | 'prev' | 'down' | 'up') => {
    let newRow = currentCell.row;
    let newCol = currentCell.col;

    switch (direction) {
      case 'next':
        newCol++;
        if (newCol >= totalCols) {
          newCol = 0;
          newRow++;
        }
        break;
      case 'prev':
        newCol--;
        if (newCol < 0) {
          newCol = totalCols - 1;
          newRow--;
        }
        break;
      case 'down':
        newRow++;
        break;
      case 'up':
        newRow--;
        break;
    }

    // Bounds checking
    newRow = Math.max(0, Math.min(totalRows - 1, newRow));
    newCol = Math.max(0, Math.min(totalCols - 1, newCol));

    setCurrentCell({ row: newRow, col: newCol });
    return { row: newRow, col: newCol };
  }, [currentCell, totalRows, totalCols]);

  return {
    currentCell,
    isEditing,
    startEditing,
    stopEditing,
    moveToNextCell,
  };
};